import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Box, Typography, CircularProgress, Avatar, Button, TextField, MenuItem, Select, FormControl, InputLabel } from '@mui/material';
import { useAuth } from '../../AuthContext';
import configData from '../../configure';
import Table from '../../components/Table';

const getAvatarColor = (name) => {
    const colors = [
        '#0ea5e9', // Blue
        '#10b981', // Teal/Green
        '#ec4899', // Pink
        '#8b5cf6', // Purple
        '#f59e0b', // Amber/Yellow
        '#f43f5e', // Rose
    ];
    let hash = 0;
    for (let i = 0; i < (name || '').length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash % colors.length);
    return colors[index];
};

const getInitials = (name) => {
    return (name || '').split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
};

export default function NirvahakStudentsTab({ selectedUser, orgId, onAddClasses }) {
    const { axios, token } = useAuth();
    
    // States for searching and filtering
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCenterFilter, setSelectedCenterFilter] = useState('ALL');
    const [selectedClassFilter, setSelectedClassFilter] = useState('ALL');

    const department = selectedUser?.rawData?.user?.additionalAttributes?.assignedClasses || selectedUser?.user?.additionalAttributes?.assignedClasses || selectedUser?.additionalAttributes?.assignedClasses || '';
    const assignedCenters = useMemo(() => {
        return department ? department.split(',').map(d => d.trim()).filter(Boolean) : [];
    }, [department]);

    // Query 1: Fetch all organization classes to map className -> classId and match sections
    const { data: allClasses, isLoading: loadingClasses } = useQuery({
        queryKey: ['allClassesComplete', orgId],
        queryFn: async () => {
            if (!orgId) return [];
            const response = await axios.get(`${configData.GET_CLASSES_BY_ORG}/${orgId}/metadata`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data || [];
        },
        enabled: !!orgId,
        staleTime: 10 * 60 * 1000,
        refetchOnWindowFocus: false,
    });

    // Resolve mappings for quick indexing
    const termToClassIds = useMemo(() => {
        const mapping = new Map();
        const addMapping = (term, classId) => {
            if (!term || !classId) return;
            // Some backends return numeric IDs; normalize to string for stable lookups.
            const clean = String(term).toLowerCase().trim();
            if (!mapping.has(clean)) {
                mapping.set(clean, []);
            }
            const arr = mapping.get(clean);
            if (!arr.includes(classId)) {
                arr.push(classId);
            }
        };

        if (allClasses) {
            allClasses.forEach(cls => {
                if (cls.classId) {
                    addMapping(cls.classId, cls.classId);
                    if (cls.className) {
                        addMapping(cls.className, cls.classId);
                    }
                    if (cls.sectionName) {
                        addMapping(cls.sectionName, cls.classId);
                    }
                    const fullLabel = `${cls.className} ${cls.sectionName || ''}`.trim();
                    addMapping(fullLabel, cls.classId);
                }
            });
        }
        return mapping;
    }, [allClasses]);

    const classIdToLabel = useMemo(() => {
        const mapping = new Map();
        if (allClasses) {
            allClasses.forEach(cls => {
                if (cls.classId) {
                    mapping.set(cls.classId, `${cls.className} ${cls.sectionName || ''}`.trim());
                }
            });
        }
        return mapping;
    }, [allClasses]);

    // Map classId -> KV Center Name (uses sectionName if defined, otherwise className)
    const classIdToCenter = useMemo(() => {
        const mapping = new Map();
        if (allClasses) {
            allClasses.forEach(cls => {
                if (cls.classId) {
                    const center = (cls.sectionName && cls.sectionName !== 'null' && cls.sectionName !== 'undefined')
                        ? String(cls.sectionName).trim()
                        : String(cls.className || '').trim();
                    mapping.set(cls.classId, center || 'Center Assigned');
                }
            });
        }
        return mapping;
    }, [allClasses]);

    // Helper: strictly resolve the student's registered Class from the student registration form (e.g. LKG, UKG, Class 1 - Class 10)
    const getStudentClassName = (stud) => {
        const registeredClass = (
            stud?.studentInfo?.className ||
            stud?.className ||
            stud?.user?.studentInfo?.className ||
            stud?.user?.additionalAttributes?.className ||
            stud?.studentInfo?.additionalAttributes?.className ||
            stud?.raw?.studentInfo?.className ||
            stud?.rawData?.studentInfo?.className ||
            stud?.rawData?.user?.additionalAttributes?.className ||
            ''
        ).trim();

        if (registeredClass && registeredClass !== 'null' && registeredClass !== 'undefined' && registeredClass !== '-') {
            return registeredClass;
        }

        return '';
    };

    // Helper: resolve the student's assigned KV Center
    const getStudentCenterName = (stud) => {
        const classId = stud?.studentInfo?.classId || stud?.classId;
        if (classId && classIdToCenter.has(classId)) {
            return classIdToCenter.get(classId);
        }
        return classIdToLabel.get(classId) || 'Center Assigned';
    };

    const assignedCenterOptions = useMemo(() => {
        return assignedCenters.map(center => {
            const cleanCenter = center.toLowerCase().trim();
            const classIds = termToClassIds.get(cleanCenter) || [];
            const centers = classIds
                .map(classId => classIdToCenter.get(classId))
                .filter(Boolean);
            const uniqueCenters = Array.from(new Set(centers));

            return {
                value: center,
                label: uniqueCenters.length > 0 ? uniqueCenters.join(', ') : center,
            };
        });
    }, [assignedCenters, termToClassIds, classIdToCenter]);

    const assignedClassIds = useMemo(() => {
        const ids = new Set();
        assignedCenters.forEach(center => {
            const classIds = termToClassIds.get(center.toLowerCase().trim()) || [];
            classIds.forEach(id => {
                ids.add(id);
            });
        });
        return Array.from(ids);
    }, [assignedCenters, termToClassIds]);

    // Query 2: Fetch students specifically for the assigned classes
    const { data: assignedStudents, isLoading: loadingStudents } = useQuery({
        queryKey: ['classStudents', assignedClassIds],
        queryFn: async () => {
            if (assignedClassIds.length === 0) return [];
            const promises = assignedClassIds.map(classId => 
                axios.get(`${configData.GET_STUDENTS_BY_CLASS}/${classId}`, {
                    params: { page: 0, pageSize: 1000 },
                    headers: { Authorization: `Bearer ${token}` }
                })
            );
            const responses = await Promise.all(promises);
            const combinedList = [];
            responses.forEach(res => {
                const list = res.data?.items || res.data?.content || res.data || [];
                combinedList.push(...list);
            });
            return combinedList;
        },
        enabled: assignedClassIds.length > 0,
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
    });

    // Standard school grade order for the dropdown
    const GRADE_ORDER = [
        "nursery", "lkg", "ukg",
        "class 1", "class 2", "class 3", "class 4", "class 5",
        "class 6", "class 7", "class 8", "class 9", "class 10"
    ];

    const sortClasses = (a, b) => {
        const idxA = GRADE_ORDER.indexOf(a.toLowerCase().trim());
        const idxB = GRADE_ORDER.indexOf(b.toLowerCase().trim());
        if (idxA !== -1 && idxB !== -1) return idxA - idxB;
        if (idxA !== -1) return -1;
        if (idxB !== -1) return 1;
        return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
    };

    // Extract all unique registered class names (from student registration form)
    const availableClassNames = useMemo(() => {
        if (!assignedStudents) return [];
        const classesSet = new Set();
        assignedStudents.forEach(stud => {
            const cls = getStudentClassName(stud);
            if (cls) {
                classesSet.add(cls);
            }
        });
        return Array.from(classesSet).sort(sortClasses);
    }, [assignedStudents]);

    // Apply real-time search queries and specific center & class filtering
    const filteredStudents = useMemo(() => {
        let list = assignedStudents || [];

        if (selectedCenterFilter !== 'ALL') {
            const targetClassIds = termToClassIds.get(selectedCenterFilter.toLowerCase().trim()) || [];
            if (targetClassIds.length > 0) {
                list = list.filter(stud => {
                    const classId = stud.studentInfo?.classId || stud.classId;
                    return targetClassIds.includes(classId);
                });
            }
        }

        if (selectedClassFilter !== 'ALL') {
            list = list.filter(stud => {
                const cls = getStudentClassName(stud).toLowerCase();
                return cls === selectedClassFilter.toLowerCase();
            });
        }

        const query = searchTerm.toLowerCase().trim();
        if (!query) return list;

        return list.filter(stud => {
            const first = stud.user?.firstName || stud.firstName || '';
            const last = stud.user?.lastName || stud.lastName || '';
            const fullName = `${first} ${last}`.toLowerCase();
            const email = (stud.user?.email || stud.email || '').toLowerCase();
            const phone = (stud.user?.phoneNumber || stud.phoneNumber || '').toLowerCase();
            const admId = (stud.studentInfo?.admissionId || stud.admissionId || '').toLowerCase();
            const cls = getStudentClassName(stud).toLowerCase();
            const center = getStudentCenterName(stud).toLowerCase();

            return fullName.includes(query) || email.includes(query) || phone.includes(query) || admId.includes(query) || cls.includes(query) || center.includes(query);
        });
    }, [assignedStudents, searchTerm, selectedCenterFilter, selectedClassFilter, termToClassIds, classIdToCenter]);

    const columns = useMemo(() => [
        {
            header: 'Name',
            accessor: 'user.firstName',
            render: (row) => {
                const first = row.user?.firstName || row.firstName || '';
                const last = row.user?.lastName || row.lastName || '';
                const fullName = `${first} ${last}`.trim() || row.email;
                const avatarColor = getAvatarColor(fullName);
                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <Avatar sx={{ bgcolor: avatarColor, width: 36, height: 36, fontWeight: 700, fontSize: '13px' }}>
                            {getInitials(fullName)}
                        </Avatar>
                        <span style={{ fontWeight: 700, color: '#1e293b' }}>{fullName}</span>
                    </div>
                );
            }
        },
        {
            header: 'Admission ID',
            accessor: 'studentInfo.admissionId',
            render: (row) => {
                const admId = row.studentInfo?.admissionId || row.admissionId;
                return admId ? (
                    <code style={{ fontFamily: 'monospace', padding: '4px 8px', backgroundColor: '#f1f5f9', color: '#475569', fontWeight: 600, fontSize: '12px', borderRadius: '4px' }}>
                        {admId}
                    </code>
                ) : '-';
            }
        },
        {
            header: 'KV Center',
            accessor: 'studentInfo.classId',
            render: (row) => {
                const centerLabel = getStudentCenterName(row);
                return (
                    <span style={{
                        padding: '4px 10px',
                        backgroundColor: '#eff6ff',
                        color: '#1d4ed8',
                        fontWeight: 600,
                        fontSize: '11px',
                        borderRadius: '6px',
                        border: '1px solid #bfdbfe',
                        display: 'inline-block',
                        whiteSpace: 'nowrap'
                    }}>
                        {centerLabel}
                    </span>
                );
            }
        },
        {
            header: 'Class',
            accessor: 'studentInfo.className',
            render: (row) => {
                const clsName = getStudentClassName(row);
                return clsName ? (
                    <span style={{
                        padding: '4px 10px',
                        backgroundColor: '#f0fdf4',
                        color: '#166534',
                        fontWeight: 600,
                        fontSize: '11px',
                        borderRadius: '6px',
                        border: '1px solid #bbf7d0',
                        display: 'inline-block',
                        whiteSpace: 'nowrap'
                    }}>
                        {clsName}
                    </span>
                ) : (
                    <span style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: '12px' }}>-</span>
                );
            }
        },
        {
            header: 'Gender',
            accessor: 'user.gender',
            render: (row) => {
                const gender = row.user?.gender || row.gender;
                if (!gender) return '-';
                const val = String(gender).trim().toUpperCase();
                if (val === 'MALE') return 'Male';
                if (val === 'FEMALE') return 'Female';
                return val.charAt(0) + val.slice(1).toLowerCase();
            }
        },
        {
            header: 'Date of Birth',
            accessor: 'user.dateOfBirth',
            render: (row) => {
                const dob = row.user?.dateOfBirth || row.dateOfBirth;
                if (!dob) return '-';
                try {
                    const d = new Date(dob);
                    return isNaN(d.getTime()) ? dob : d.toLocaleDateString();
                } catch {
                    return dob;
                }
            }
        },
        {
            header: 'Phone No',
            accessor: 'user.phoneNumber',
            render: (row) => {
                const phone = row.user?.phoneNumber || row.phoneNumber;
                return phone ? `📞 ${phone}` : '-';
            }
        },
        {
            header: 'Email',
            accessor: 'user.email',
            render: (row) => {
                const email = row.user?.email || row.email;
                return email ? `✉️ ${email}` : '-';
            }
        }
    ], [classIdToLabel, classIdToCenter]);

    const isLoading = loadingClasses || loadingStudents;

    if (isLoading) {
        return (
            <Box sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (assignedCenters.length === 0) {
        return (
            <Box sx={{ p: 4, textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
                <Typography variant="body1" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    No classes/centers assigned to this Nirvahak yet.
                </Typography>
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => onAddClasses?.()}
                    disabled={!onAddClasses}
                    sx={{
                        textTransform: 'none',
                        fontWeight: 700,
                        fontSize: '11px',
                        py: 0.2,
                        px: 1,
                        height: '22px',
                        minWidth: 'auto',
                        borderColor: '#cbd5e1',
                        color: '#475569',
                        borderRadius: '6px',
                        '&:hover': {
                            borderColor: '#0070D2',
                            color: '#0070D2',
                            bgcolor: '#f0f9ff'
                        }
                    }}
                >
                    ✏️ Add
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 4 }}>
            {/* Dynamic Metric Summaries */}
            <Box sx={{ display: 'flex', gap: 3, mb: 4, flexWrap: 'wrap' }}>
                {/* Total Students Metric Card */}
                <Box sx={{ flex: 1, minWidth: '220px', borderRadius: '16px', border: '1px solid #e2e8f0', p: '20px 24px', bgcolor: '#ffffff' }}>
                    <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#64748b', letterSpacing: '1px', textTransform: 'uppercase', mb: 1 }}>
                        Overseen Students
                    </Typography>
                    <Typography sx={{ fontSize: '32px', fontWeight: 800, color: '#1e293b', lineHeight: 1.2 }}>
                        {(assignedStudents || []).length}
                    </Typography>
                    <Typography sx={{ fontSize: '13px', color: '#64748b', mt: 1 }}>
                        Across all assigned tuition centers
                    </Typography>
                </Box>

                {/* Centers Count Metric Card */}
                <Box sx={{ flex: 1, minWidth: '220px', borderRadius: '16px', border: '1px solid #e2e8f0', p: '20px 24px', bgcolor: '#ffffff' }}>
                    <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#64748b', letterSpacing: '1px', textTransform: 'uppercase', mb: 1 }}>
                        Active Centers
                    </Typography>
                    <Typography sx={{ fontSize: '32px', fontWeight: 800, color: '#0070D2', lineHeight: 1.2 }}>
                        {assignedCenterOptions.length}
                    </Typography>
                    <Typography sx={{ fontSize: '13px', color: '#64748b', mt: 1 }}>
                        Configured regions
                    </Typography>
                </Box>
            </Box>

            {/* Premium Filter Controls Bar */}
            <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField
                    size="small"
                    placeholder="🔍 Search students by name, ID, phone..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{ flex: 1, minWidth: '250px', bgcolor: '#fff', borderRadius: '8px' }}
                />
                
                {assignedCenterOptions.length >= 1 && (
                    <FormControl size="small" sx={{ minWidth: '200px', bgcolor: '#fff', borderRadius: '8px' }}>
                        <InputLabel id="center-filter-label">Filter by Center</InputLabel>
                        <Select
                            labelId="center-filter-label"
                            value={selectedCenterFilter}
                            label="Filter by Center"
                            onChange={(e) => setSelectedCenterFilter(e.target.value)}
                        >
                            <MenuItem value="ALL">All Centers</MenuItem>
                            {assignedCenterOptions.map(center => (
                                <MenuItem key={center.value} value={center.value}>{center.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}

                {availableClassNames.length >= 1 && (
                    <FormControl size="small" sx={{ minWidth: '180px', bgcolor: '#fff', borderRadius: '8px' }}>
                        <InputLabel id="class-filter-label">Filter by Class</InputLabel>
                        <Select
                            labelId="class-filter-label"
                            value={selectedClassFilter}
                            label="Filter by Class"
                            onChange={(e) => setSelectedClassFilter(e.target.value)}
                        >
                            <MenuItem value="ALL">All Classes</MenuItem>
                            {availableClassNames.map(cls => (
                                <MenuItem key={cls} value={cls}>{cls}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}
            </Box>

            {/* Students List Header */}
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, color: '#1e293b' }}>
                Assigned Student Roster ({filteredStudents.length})
            </Typography>

            {/* Students Table */}
            {filteredStudents.length > 0 ? (
                <Table
                    data={filteredStudents}
                    columns={columns}
                    loading={isLoading}
                    enableCheckboxes={false}
                    enableSorting={true}
                    enableColumnToggle={false}
                    enableExport={false}
                    hoverable
                    striped
                    className="kv-table-premium"
                />
            ) : (
                <Box sx={{ p: 4, bgcolor: '#f8fafc', border: '1px dashed #e2e8f0', borderRadius: '12px', textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        No students found matching your criteria.
                    </Typography>
                </Box>
            )}
        </Box>
    );
}
